import Image from 'next/image'
import clsx from 'clsx'
import { useRouter } from 'next/router'
import { GetServerSideProps } from 'next'

/* blocks */
import Link from '@/atoms/Link/Link'
import Layout from '@/templates/Layout/Layout'

/* utils */
import { getStrapiGraphqlData } from '@/utils/api/wrapper'
import { resourceListQuery } from '@/utils/queries/resources'

/* styling */
import styles from '@/styles/resource.module.css'

interface Props {
  resources: ResourcesPageAttributes[]
}

const resourceOptions = {
  '': {
    label: 'All Resources',
    value: '',
    display: '',
    showNav: true
  },
  ebook: {
    label: 'E-books',
    value: 'ebook',
    display: 'E-book',
    showNav: true
  },
  guideline: {
    label: 'Guidelines',
    value: 'guideline',
    display: 'Guideline',
    showNav: false
  },
  whitepaper: {
    label: 'Whitepapers',
    value: 'whitepaper',
    display: 'Whitepaper',
    showNav: false
  },
  template: {
    label: 'Templates',
    value: 'template',
    display: 'Template',
    showNav: false
  }
}

const metadata = [
  {
    property: 'description',
    content:
      'Learn about content creation and expert guides to help you and your business grow.'
  },
  {
    property: 'og:title',
    content: 'Resources To Help You & Your Business Grow | Pepper Content'
  },
  {
    property: 'og:description',
    content:
      'Learn about content creation and expert guides to help you and your business grow.'
  }
]

function ResourcesPage({ resources }: Props): JSX.Element {
  const router = useRouter()

  const resourceType = router.query?.type ?? ''

  return (
    <Layout
      title="Resources To Help You & Your Business Grow | Pepper Content"
      meta={metadata}
      canonical="/resources/"
    >
      <section className="max-width-container  |  pt-8 pb-6 md:pt-16 md:pb-12">
        <h1 className="text-h2 md:text-h1--alt font-medium text-center  |  mb-4">
          Resources by Pepper Content
        </h1>
        <p className="text-paragraph-3 md:text-paragraph-2 text-gray-60 text-center">
          Dive deep with thoughtful content and expert guides to help you grow.
        </p>
      </section>

      <section className="sm:shadow-sm">
        <div className="hidden sm:block  |  max-w-fit mx-auto  |  overflow-x-auto  |  px-5">
          <div className="flex items-center gap-8">
            {Object.values(resourceOptions)
              .filter(option => option.showNav)
              .map(option => (
                <Link
                  key={option.value}
                  href={
                    option.value === ''
                      ? `/resources/`
                      : `?type=${option.value}`
                  }
                  className={clsx(
                    'text-paragraph-3 font-medium text-gray-60 whitespace-nowrap  |  py-5  |  focus-visible-ring',
                    resourceType === option.value &&
                      'text-blue-50 shadow-[inset_0px_-2px_0px_#7566FF]'
                  )}
                >
                  {option.label}
                </Link>
              ))}
          </div>
        </div>
        <div className="max-width-container  |  sm:hidden">
          <select
            name="Resource Filter"
            id="resource-filter"
            onChange={e => router.push(`?type=${e.target.value}`)}
            className="text-gray-80 bg-select-arrow bg-no-repeat bg-[center_right_1rem]  |  border border-gray-40 rounded  | py-2 px-4  |  w-full  |  appearance-none"
            value={resourceType}
          >
            {Object.values(resourceOptions)
              .filter(option => option.showNav)
              .map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
          </select>
        </div>
      </section>

      <section className="max-width-container  |  grid sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-8  |  pt-6 md:pt-12 pb-28">
        {resources.map(resource => {
          const image = resource.heroImage.data.attributes
          return (
            <Link
              key={resource.title}
              href={`/resources/${resource.type}/${resource.slug}`}
              className="group focus-visible-ring"
            >
              <article>
                <div
                  className="flex items-center justify-center  |  rounded-lg  |  h-[300px] overflow-hidden  |  mb-6"
                  style={{ backgroundColor: resource.featuredHexcode }}
                >
                  <div
                    className={clsx(
                      'relative top-12 sm:top-0  |  w-[218px] h-[288px] sm:h-[244px] sm:w-[188px]',
                      styles.imageContainer
                    )}
                  >
                    <Image
                      priority={true}
                      src={image.url}
                      alt={image.alternativeText}
                      width={image.width}
                      height={image.height}
                    />
                  </div>
                </div>
                <p className="text-caption font-medium tracking-wider uppercase  |  mb-2">
                  {resourceOptions[resource.type].display}
                </p>
                <h2 className="text-paragraph-1 font-medium  |  mb-2  | group-hover:text-blue-50 group-focus:text-blue-50">
                  {resource.title}
                </h2>
                <p className="text-paragraph-4 text-gray-70  |  mb-4">
                  {resource.description}
                </p>
                <p className="text-caption font-medium tracking-wider text-blue-50 uppercase">
                  Download FREE
                </p>
              </article>
            </Link>
          )
        })}
      </section>
    </Layout>
  )
}

export const getServerSideProps: GetServerSideProps = async ({
  res,
  query
}) => {
  res.setHeader('Cache-Control', 'public, s-maxage=600, stale-while-revalidate')

  const result = await getStrapiGraphqlData<ResourcePageQuery | undefined>(
    resourceListQuery.loc?.source.body,
    {
      type: query.type || undefined
    }
  )

  return {
    props: {
      resources: result?.resources.data.map(item => item.attributes) ?? []
    }
  }
}

export default ResourcesPage
